/**
 * @file: MediaAttachmentGrid.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Read-only thumbnail grid for a lead activity / discussion message's
 * `LeadMediaAttachment[]`. Images show their cover (blurhash placeholder rides
 * the cover url fragment); videos show the cover plus a play badge. Tapping a
 * tile opens the shared full-screen media preview.
 */

import { AppNavParams } from '@euler/app/Routes';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadMediaAttachment } from '@euler/model/lead/attachment';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Play } from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

// A video by kind OR mime (older rows can be mislabeled at pick time).
const isVideoAttachment = (m: LeadMediaAttachment) =>
  m.kind === 'video' || (m.mimeType ?? '').startsWith('video/');

export const MediaAttachmentGrid = memo(
  ({ attachments }: { attachments: LeadMediaAttachment[] }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    const previewImage = usePreviewImage();
    if (!attachments.length) return null;
    const tile = rpx(76);
    // The image urls, in order — the viewer pages across them, opening at the
    // tapped image. Videos open in the dedicated player route instead.
    const imageUrls = attachments
      .filter(m => !isVideoAttachment(m))
      .map(m => m.url);
    return (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: rpx(6),
          marginTop: rpx(8),
        }}
      >
        {attachments.map(media => {
          const isVideo = isVideoAttachment(media);
          const thumb = media.coverUrl || media.url;
          return (
            <TouchableOpacity
              key={media.id}
              activeOpacity={0.85}
              onPress={() => {
                if (isVideo) {
                  navigation.navigate('_mediaPreview', {
                    type: 'video',
                    url: media.url,
                  });
                } else {
                  const index = imageUrls.indexOf(media.url);
                  previewImage({
                    imageUrls,
                    index: index < 0 ? 0 : index,
                  });
                }
              }}
              style={{
                width: tile,
                height: tile,
                borderRadius: rpx(10),
                overflow: 'hidden',
                backgroundColor: LD.hairline,
              }}
            >
              <Img
                uri={thumb}
                resizeMode="cover"
                style={{ width: tile, height: tile }}
              />
              {isVideo ? (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <View
                    style={{
                      width: rpx(30),
                      height: rpx(30),
                      borderRadius: rpx(15),
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Play color="#fff" size={rpx(15)} fill="#fff" />
                  </View>
                </View>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>
    );
  },
);
